import type { InviteMemberInput } from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { useAuthClient } from '../auth-context.tsx'
import { memberKeys } from './keys.ts'

/**
 * Staff & roles screen data. Reads come from our API; membership *writes*
 * (invite, remove, role change) go through better-auth's org endpoints — one
 * write path (see members.routes.ts). Campus scoping is ours.
 */

export function useMembers() {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: memberKeys.list(),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.members.get({
        query: pageParam ? { cursor: pageParam } : {},
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })

  return {
    members: query.data?.pages.flatMap((p) => p.items) ?? [],
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isLoading: query.isPending,
    isError: query.isError,
  }
}

export function useRoles() {
  const api = useApi()
  return useQuery({
    queryKey: memberKeys.roles(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.roles.get()
      if (error) throw error
      return data
    },
    // The catalog is code-defined server-side; it changes on deploy, not at runtime.
    staleTime: Infinity,
  })
}

interface AuthResult {
  error?: { message?: string } | null
}

function unwrap<T extends AuthResult>(result: T, fallback: string): T {
  if (result.error) throw new Error(result.error.message ?? fallback)
  return result
}

/**
 * Invites someone by email, as a role. With `personId` (a person's "Invite
 * to sign in"), the invitation is for that directory person: accepting it
 * links them to the new login and moves the access waiting for them.
 */
export function useInviteMember() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: InviteMemberInput & { personId?: string }) =>
      unwrap(
        await authClient.organization.inviteMember({
          email: input.email,
          // better-auth's client types the role as its built-in union; our
          // role ids are registered in the server's access controller.
          role: input.role as 'member',
          ...(input.personId !== undefined && { personId: input.personId }),
        }),
        'Could not send the invitation',
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: memberKeys.invitations() }),
  })
}

/**
 * Sends a pending invitation's email again and gives it a fresh expiry —
 * better-auth's invite with `resend`, which reuses the invitation (same link)
 * rather than making a second one.
 */
export function useResendInvitation() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: InviteMemberInput) =>
      unwrap(
        await authClient.organization.inviteMember({
          email: input.email,
          role: input.role as 'member',
          resend: true,
        }),
        'Could not resend the invitation',
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: memberKeys.invitations() }),
  })
}

export function useInvitations() {
  const authClient = useAuthClient()
  return useQuery({
    queryKey: memberKeys.invitations(),
    queryFn: async () => {
      const result = unwrap(
        await authClient.organization.listInvitations(),
        'Could not load invitations',
      )
      return (result.data ?? []).filter((i) => i.status === 'pending')
    },
  })
}

export function useCancelInvitation() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (invitationId: string) =>
      unwrap(
        await authClient.organization.cancelInvitation({ invitationId }),
        'Could not cancel the invitation',
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: memberKeys.invitations() }),
  })
}

export function useRemoveMember() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (memberIdOrEmail: string) =>
      unwrap(
        await authClient.organization.removeMember({ memberIdOrEmail }),
        'Could not remove the member',
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: memberKeys.all() }),
  })
}

export function useUpdateMemberRole() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { memberId: string; roles: string[] }) =>
      unwrap(
        await authClient.organization.updateMemberRole({
          memberId: input.memberId,
          role: input.roles as 'member'[],
        }),
        'Could not change the role',
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: memberKeys.all() }),
  })
}

/** Campus scoping — ours, not better-auth's (`PUT /members/:id/campuses`). */
export function useSetMemberCampuses() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { memberId: string; campusIds: string[] | null }) => {
      const { data, error } = await api.api.v1
        .members({ id: input.memberId })
        .campuses.put({ campusIds: input.campusIds })
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: memberKeys.all() }),
  })
}
